/**
 * Pictures of worked ground: fields, orchards, plantations, mines, quarries.
 *
 * The old marks — three furrow lines, a caret, a stroke — told you *that* a tile had
 * been worked. These tell you what it has become. Farmland is the clearest case: a
 * patchwork of plots in different crops with hedgerows between them is the single most
 * recognisable thing a settled landscape has, and a map where the land round a city
 * turns into fields as workers improve it shows the civilisation growing in a way no
 * number in a panel does.
 *
 * Two families, drawn differently on purpose:
 *
 *   GROUND COVER — fields, orchard, plantation. Clipped to an inset hex so a margin of
 *   wild ground shows at the edge, and the tile still reads as grassland-with-a-farm
 *   rather than as a farm-coloured hex. Each is laid at its own slight angle, from the
 *   tile index, because a countryside of perfectly aligned fields looks printed.
 *
 *   WORKINGS — mine, quarry. Objects standing on the ground, set to the lower-left of
 *   the tile so the resource picture that usually justifies them (iron, stone) stays
 *   visible at the centre.
 *
 * Roads are neither, and are not here: a road is a network, drawn by the renderer from
 * each tile's neighbours, not a picture on one tile. `DRAWERS.road` is deliberately a
 * no-op so the record stays complete.
 *
 * Same language as everything else — flat fills, one ink outline, light from the
 * north-west — and still no image assets.
 */

import type { ImprovementArt } from '../../content/schema/index.js';
import { parseHex, shade, tileJitter, tileNoise, toCss, type Point } from './cartography.js';

type Ctx = CanvasRenderingContext2D;
type Art = (ctx: Ctx, x: number, y: number, size: number, index: number, corners: readonly Point[]) => void;

const INK = 'rgba(24, 18, 12, 0.85)';

function tone(colour: string, amount: number): string {
  return toCss(shade(parseHex(colour), amount));
}

function ink(ctx: Ctx, size: number, weight: number): void {
  ctx.strokeStyle = INK;
  ctx.lineWidth = Math.max(1, size * weight);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.stroke();
}

/**
 * Clip to a hex shrunk toward its centre, then turn the frame to the tile's own angle.
 * Callers must `restore` — every ground-cover drawer is wrapped in save/restore.
 */
function plot(ctx: Ctx, x: number, y: number, corners: readonly Point[], inset: number, angle: number): void {
  ctx.beginPath();
  corners.forEach((c, i) => (i === 0 ? ctx.moveTo(x + c.x * inset, y + c.y * inset) : ctx.lineTo(x + c.x * inset, y + c.y * inset)));
  ctx.closePath();
  ctx.clip();
  ctx.translate(x, y);
  ctx.rotate(angle);
}

/** How far a tile's cultivation is turned from square. Small: fields follow the land, not the grid. */
export function fieldAngle(index: number): number {
  return (tileNoise(index, 110) - 0.5) * 0.9;
}

/**
 * Where a tile's fields divide. Returned as fractions of the tile size so the maths is
 * testable without a canvas; never so close to the edge that a plot vanishes.
 */
export function fieldSplit(index: number): { x: number; y: number } {
  return { x: tileJitter(index, 111, 0.24), y: tileJitter(index, 112, 0.24) };
}

const CROPS = ['#BFAE5A', '#8FA64A', '#D0BB68', '#7C9544', '#A99C4E', '#9BB257'];

// ─── ground cover ──────────────────────────────────────────────────────────────

/** A patchwork of four plots in different crops, furrowed, with hedgerows between. */
const fields: Art = (ctx, x, y, size, index, corners) => {
  ctx.save();
  plot(ctx, x, y, corners, 0.84, fieldAngle(index));

  const split = fieldSplit(index);
  const sx = split.x * size;
  const sy = split.y * size;
  const far = size * 1.2;
  const plots: Array<[number, number, number, number]> = [
    [-far, -far, sx + far, sy + far],
    [sx, -far, far - sx, sy + far],
    [-far, sy, sx + far, far - sy],
    [sx, sy, far - sx, far - sy],
  ];

  plots.forEach(([left, top, width, height], i) => {
    const crop = CROPS[Math.floor(tileNoise(index, 113 + i) * CROPS.length)]!;
    ctx.fillStyle = crop;
    ctx.fillRect(left, top, width, height);

    // Furrows, running across on alternate plots so neighbours read as separate fields.
    ctx.strokeStyle = tone(crop, -0.2);
    ctx.lineWidth = Math.max(0.8, size * 0.022);
    const across = (i + Math.floor(tileNoise(index, 117) * 2)) % 2 === 0;
    const step = size * 0.1;
    ctx.beginPath();
    if (across) {
      for (let fy = top + step / 2; fy < top + height; fy += step) {
        ctx.moveTo(left, fy);
        ctx.lineTo(left + width, fy);
      }
    } else {
      for (let fx = left + step / 2; fx < left + width; fx += step) {
        ctx.moveTo(fx, top);
        ctx.lineTo(fx, top + height);
      }
    }
    ctx.stroke();
  });

  // Hedgerows along the divisions: what turns four coloured rectangles into farmland.
  ctx.beginPath();
  ctx.moveTo(sx, -far);
  ctx.lineTo(sx, far);
  ctx.moveTo(-far, sy);
  ctx.lineTo(far, sy);
  ctx.strokeStyle = 'rgba(38, 60, 30, 0.8)';
  ctx.lineWidth = Math.max(1.5, size * 0.055);
  ctx.stroke();

  ctx.restore();
};

/** Trees in rows — the regularity is the whole point; it is what separates an orchard from a wood. */
const orchard: Art = (ctx, x, y, size, index, corners) => {
  ctx.save();
  plot(ctx, x, y, corners, 0.84, fieldAngle(index));

  ctx.fillStyle = 'rgba(152, 180, 98, 0.6)';
  ctx.fillRect(-size, -size, size * 2, size * 2);

  const gap = size * 0.3;
  const r = size * 0.095;
  const trees: Point[] = [];
  for (let row = -3; row <= 3; row++) {
    for (let col = -3; col <= 3; col++) {
      const tx = col * gap + (row % 2 === 0 ? 0 : gap / 2);
      const ty = row * gap * 0.86;
      if (Math.hypot(tx, ty) < size * 0.84) trees.push({ x: tx, y: ty });
    }
  }

  ctx.fillStyle = 'rgba(12, 26, 14, 0.36)';
  for (const t of trees) {
    ctx.beginPath();
    ctx.arc(t.x + size * 0.04, t.y + size * 0.05, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = '#4C7E39';
  for (const t of trees) {
    ctx.beginPath();
    ctx.arc(t.x, t.y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = 'rgba(146, 190, 104, 0.55)';
  for (const t of trees) {
    ctx.beginPath();
    ctx.arc(t.x - r * 0.3, t.y - r * 0.3, r * 0.45, 0, Math.PI * 2);
    ctx.fill();
  }
  // A little fruit on some of them. Stable per tree, so the harvest does not flicker.
  ctx.fillStyle = '#C4523A';
  trees.forEach((t, i) => {
    if (tileNoise(index, 120 + i) < 0.55) return;
    ctx.beginPath();
    ctx.arc(t.x + r * 0.35, t.y + r * 0.1, Math.max(0.8, size * 0.02), 0, Math.PI * 2);
    ctx.fill();
  });

  ctx.restore();
};

/** Long rows of low bushes on bare soil — tea, cotton, indigo. Rows, not trees. */
const plantation: Art = (ctx, x, y, size, index, corners) => {
  ctx.save();
  plot(ctx, x, y, corners, 0.84, fieldAngle(index) + Math.PI / 2);

  ctx.fillStyle = 'rgba(124, 94, 62, 0.8)';
  ctx.fillRect(-size, -size, size * 2, size * 2);

  const spacing = size * 0.2;
  ctx.lineCap = 'round';
  for (let k = -5; k <= 5; k++) {
    const ry = k * spacing;
    ctx.beginPath();
    ctx.moveTo(-size, ry);
    ctx.lineTo(size, ry);
    ctx.strokeStyle = 'rgba(20, 30, 14, 0.4)';
    ctx.lineWidth = Math.max(2, size * 0.12);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(-size, ry - size * 0.015);
    ctx.lineTo(size, ry - size * 0.015);
    ctx.strokeStyle = '#5B8C3C';
    ctx.lineWidth = Math.max(1.5, size * 0.1);
    ctx.stroke();

    // Break each row into bushes with a dashed highlight along its top.
    ctx.setLineDash([size * 0.05, size * 0.07]);
    ctx.lineDashOffset = tileNoise(index, 130 + k) * size * 0.12;
    ctx.beginPath();
    ctx.moveTo(-size, ry - size * 0.035);
    ctx.lineTo(size, ry - size * 0.035);
    ctx.strokeStyle = 'rgba(150, 196, 104, 0.6)';
    ctx.lineWidth = Math.max(1, size * 0.04);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  ctx.restore();
};

// ─── workings ──────────────────────────────────────────────────────────────────

/** A timbered adit into a mound, a spoil heap beside it and a cart track out. */
const mine: Art = (ctx, x, y, size) => {
  // Larger than the quarry: at 0.3 the adit was a brown smudge beside the resource
  // picture, and a mine is the improvement a player most wants to spot on a hill.
  const s = size * 0.36;
  const cx = x - size * 0.34;
  const cy = y + size * 0.32;

  ctx.save();

  ctx.beginPath();
  ctx.ellipse(cx + s * 0.4, cy + s * 0.62, s * 1.6, s * 0.3, 0, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(20, 16, 10, 0.3)';
  ctx.fill();

  // Spoil heap.
  ctx.beginPath();
  ctx.moveTo(cx + s * 0.35, cy + s * 0.55);
  ctx.quadraticCurveTo(cx + s * 1.0, cy - s * 0.45, cx + s * 1.65, cy + s * 0.55);
  ctx.closePath();
  ctx.fillStyle = '#8E7C63';
  ctx.fill();
  ink(ctx, s, 0.08);
  ctx.fillStyle = 'rgba(40, 32, 22, 0.55)';
  for (const [px, py] of [[0.85, 0.2], [1.1, 0.35], [1.3, 0.15], [0.95, 0.42]] as const) {
    ctx.beginPath();
    ctx.arc(cx + px * s, cy + py * s, s * 0.06, 0, Math.PI * 2);
    ctx.fill();
  }

  // The mound the adit is driven into.
  ctx.beginPath();
  ctx.ellipse(cx, cy + s * 0.55, s * 0.85, s * 0.95, 0, Math.PI, Math.PI * 2);
  ctx.closePath();
  ctx.fillStyle = '#7B6B56';
  ctx.fill();
  ink(ctx, s, 0.08);
  ctx.beginPath();
  ctx.ellipse(cx - s * 0.22, cy + s * 0.2, s * 0.35, s * 0.3, -0.4, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255, 240, 210, 0.16)';
  ctx.fill();

  // The opening.
  ctx.beginPath();
  ctx.moveTo(cx - s * 0.34, cy + s * 0.55);
  ctx.lineTo(cx - s * 0.34, cy + s * 0.02);
  ctx.quadraticCurveTo(cx, cy - s * 0.32, cx + s * 0.34, cy + s * 0.02);
  ctx.lineTo(cx + s * 0.34, cy + s * 0.55);
  ctx.closePath();
  ctx.fillStyle = '#17110B';
  ctx.fill();

  // Timbers: cased in ink, then the wood.
  const frame = (): void => {
    ctx.beginPath();
    ctx.moveTo(cx - s * 0.36, cy + s * 0.55);
    ctx.lineTo(cx - s * 0.36, cy - s * 0.1);
    ctx.moveTo(cx + s * 0.36, cy + s * 0.55);
    ctx.lineTo(cx + s * 0.36, cy - s * 0.1);
    ctx.moveTo(cx - s * 0.5, cy - s * 0.1);
    ctx.lineTo(cx + s * 0.5, cy - s * 0.1);
  };
  frame();
  ctx.strokeStyle = INK;
  ctx.lineWidth = Math.max(2, s * 0.22);
  ctx.lineCap = 'round';
  ctx.stroke();
  frame();
  ctx.strokeStyle = '#A57D4B';
  ctx.lineWidth = Math.max(1, s * 0.12);
  ctx.stroke();

  // Cart track running out of the adit.
  ctx.beginPath();
  ctx.moveTo(cx - s * 0.14, cy + s * 0.55);
  ctx.lineTo(cx - s * 0.3, cy + s * 1.05);
  ctx.moveTo(cx + s * 0.14, cy + s * 0.55);
  ctx.lineTo(cx + s * 0.12, cy + s * 1.05);
  ctx.strokeStyle = 'rgba(40, 30, 20, 0.7)';
  ctx.lineWidth = Math.max(1, s * 0.07);
  ctx.stroke();

  ctx.restore();
};

/** A pit cut in steps, pale stone showing, and a couple of blocks lifted out. */
const quarry: Art = (ctx, x, y, size) => {
  const s = size * 0.33;
  const cx = x - size * 0.3;
  const cy = y + size * 0.3;

  ctx.save();

  const terrace = (inset: number, drop: number, fill: string): void => {
    const left = cx - s * (1.1 - inset);
    const top = cy - s * (0.6 - inset) + drop * s;
    const width = s * (2.2 - inset * 2);
    const height = s * (1.2 - inset * 2);
    ctx.beginPath();
    ctx.moveTo(left + s * 0.12, top);
    ctx.lineTo(left + width - s * 0.12, top);
    ctx.lineTo(left + width, top + height);
    ctx.lineTo(left, top + height);
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
    ink(ctx, s, 0.07);
    // The shadow each cut face throws on the step below it.
    ctx.fillStyle = 'rgba(30, 26, 20, 0.3)';
    ctx.fillRect(left + s * 0.1, top + s * 0.02, width - s * 0.2, s * 0.1);
  };

  terrace(0, 0, '#C2BAA8');
  terrace(0.25, 0.06, '#ABA391');
  terrace(0.48, 0.1, '#958D7C');

  // Two cut blocks lifted out onto the lip.
  const block = (bx: number, by: number, w: number): void => {
    const h = w * 0.62;
    const d = h * 0.4;
    ctx.beginPath();
    ctx.rect(bx, by, w, h);
    ctx.fillStyle = '#B5AD9C';
    ctx.fill();
    ink(ctx, s, 0.06);
    ctx.beginPath();
    ctx.moveTo(bx, by);
    ctx.lineTo(bx + d, by - d);
    ctx.lineTo(bx + w + d, by - d);
    ctx.lineTo(bx + w, by);
    ctx.closePath();
    ctx.fillStyle = '#D3CCBC';
    ctx.fill();
    ink(ctx, s, 0.06);
  };
  block(cx + s * 1.15, cy + s * 0.1, s * 0.45);
  block(cx + s * 1.3, cy + s * 0.55, s * 0.4);

  ctx.restore();
};

const nothing: Art = () => {};

/** Every drawing the content may ask for. Complete by type, so a new name must be drawn. */
export const IMPROVEMENT_DRAWERS: Readonly<Record<ImprovementArt, Art>> = {
  none: nothing,
  fields,
  orchard,
  plantation,
  mine,
  quarry,
  // Drawn as a network by the renderer, from the tile's neighbours.
  road: nothing,
};

export function drawImprovementArt(
  ctx: Ctx,
  art: ImprovementArt,
  x: number,
  y: number,
  size: number,
  index: number,
  corners: readonly Point[],
): void {
  IMPROVEMENT_DRAWERS[art](ctx, x, y, size, index, corners);
}
